/**
 * src/utils/audioHaptics.ts
 * Synthesized Web Audio API touch sounds and mobile haptic vibration
 * Zero external audio files — 100% responsive, high-performance, and configurable.
 */

// Global AudioContext reference (lazily initialized upon user interaction)
let audioCtx: AudioContext | null = null;

function getAudioContext(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (!audioCtx) {
    const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
    if (AudioContextClass) {
      audioCtx = new AudioContextClass();
    }
  }
  if (audioCtx && audioCtx.state === 'suspended') {
    audioCtx.resume().catch(() => {});
  }
  return audioCtx;
}

// Preference getters & setters
export function isSoundEnabled(): boolean {
  if (typeof window === 'undefined') return true;
  const val = localStorage.getItem('ckaudit_sound_enabled');
  return val === null ? true : val === 'true';
}

export function setSoundEnabled(enabled: boolean): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem('ckaudit_sound_enabled', String(enabled));
}

export function isHapticsEnabled(): boolean {
  if (typeof window === 'undefined') return true;
  const val = localStorage.getItem('ckaudit_haptics_enabled');
  return val === null ? true : val === 'true';
}

export function setHapticsEnabled(enabled: boolean): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem('ckaudit_haptics_enabled', String(enabled));
}

/**
 * Plays a subtle, tactile mechanical Apple-style click sound
 */
export function playTapSound(): void {
  if (!isSoundEnabled()) return;
  try {
    const ctx = getAudioContext();
    if (!ctx) return;

    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    const filter = ctx.createBiquadFilter();

    // Highpass to eliminate muddy low rumble, keep snappy click
    filter.type = 'highpass';
    filter.frequency.setValueAtTime(320, ctx.currentTime);

    // Fast frequency drop (1100Hz -> 180Hz) creates a crisp mechanical micro-switch impulse
    osc.type = 'sine';
    osc.frequency.setValueAtTime(1100, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(180, ctx.currentTime + 0.022);

    // Exponential decay gain envelope (very quiet and refined)
    gain.gain.setValueAtTime(0.045, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.025);

    osc.connect(filter);
    filter.connect(gain);
    gain.connect(ctx.destination);

    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.028);
  } catch {}
}

/**
 * Plays an Apple Pay style ascending dual-tone chime
 */
export function playSuccessSound(): void {
  if (!isSoundEnabled()) return;
  try {
    const ctx = getAudioContext();
    if (!ctx) return;

    const now = ctx.currentTime;
    [523.25, 659.25, 783.99].forEach((freq, idx) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, now + idx * 0.06);

      gain.gain.setValueAtTime(0, now + idx * 0.06);
      gain.gain.linearRampToValueAtTime(0.06, now + idx * 0.06 + 0.015);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + idx * 0.06 + 0.16);

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.start(now + idx * 0.06);
      osc.stop(now + idx * 0.06 + 0.18);
    });
  } catch {}
}

/**
 * Plays a gentle, double knock alert
 */
export function playErrorSound(): void {
  if (!isSoundEnabled()) return;
  try {
    const ctx = getAudioContext();
    if (!ctx) return;

    const now = ctx.currentTime;
    [180, 140].forEach((freq, idx) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = 'triangle';
      osc.frequency.setValueAtTime(freq, now + idx * 0.08);

      gain.gain.setValueAtTime(0.05, now + idx * 0.08);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + idx * 0.08 + 0.06);

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.start(now + idx * 0.08);
      osc.stop(now + idx * 0.08 + 0.07);
    });
  } catch {}
}

/**
 * Triggers physical device vibration if supported by hardware
 */
export function triggerHaptic(type: 'light' | 'medium' | 'heavy' | 'success' | 'error' = 'light'): void {
  if (!isHapticsEnabled()) return;
  if (typeof navigator === 'undefined' || !('vibrate' in navigator)) return;

  try {
    switch (type) {
      case 'light':
        navigator.vibrate(10);
        break;
      case 'medium':
        navigator.vibrate(20);
        break;
      case 'heavy':
        navigator.vibrate(35);
        break;
      case 'success':
        navigator.vibrate([12, 35, 20]);
        break;
      case 'error':
        navigator.vibrate([25, 30, 25]);
        break;
    }
  } catch {}
}

/**
 * Combined high-level tactile triggers
 */
export const haptic = {
  tap: () => {
    playTapSound();
    triggerHaptic('light');
  },
  light: () => {
    triggerHaptic('light');
  },
  selection: () => {
    playTapSound();
    triggerHaptic('light');
  },
  button: () => {
    playTapSound();
    triggerHaptic('medium');
  },
  success: () => {
    playSuccessSound();
    triggerHaptic('success');
  },
  error: () => {
    playErrorSound();
    triggerHaptic('error');
  }
};
